<template>
    <div class="w-100 h-100">
        <b-modal
            v-model="showModal"
            hide-header
            hide-header-close
            title="Data Submitted"
            centered
            @ok="clearFields"
        >
            <div class="text-center">
                <div>{{ModalMessage}}</div>
            </div>
        </b-modal>
        <b-container fluid>
            <h2 class="tw-mb-0 text-aegis-dk">Blood Sample Review</h2>
            <p class="tw-italic tw-text-gray-700">
                Blood sample processing
            </p>
        </b-container>
        <br />
        <b-row>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-1"
                    label="Visit Type"
                    label-for="visit_type"
                >
                    <b-form-select id="visit_type" v-model="filterVisitType" :options="visitTypeOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-2"
                    label="Sample Type"
                    label-for="sample_type"
                >
                    <b-form-select id="sample_type" v-model="filterSampleType" :options="sampleTypeOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-3"
                    label="Box Number"
                    label-for="box"
                >
                    <b-form-select id="box" v-model="filterBoxNum" :options="BoxNumOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-4"
                    label="Freezer Number"
                    label-for="freezer"
                >
                    <b-form-select id="freezer" v-model="filterFreezerNum" :options="FreezerNumOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-5"
                    label="Shelf Number"
                    label-for="shelf"
                >
                    <b-form-select id="shelf" v-model="filterShelfNum" :options="shelfNumOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-6"
                    label="Status"
                    label-for="status"
                >
                    <b-form-select id="status" v-model="filterStatus" :options="statusOptions"></b-form-select>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="2">
                <b-form-group
                    id="fieldset-7"
                    label="Subject ID"
                    label-for="subject_id"
                >
                    <b-input-group>
                        <b-form-input
                            id="subject_id"
                            v-model="subject_id"
                            trim v-on:keydown.enter="updateTable"
                            placeholder="Search for Subject ID..."
                        ></b-form-input>
                        <b-input-group-append>
                            <b-button variant="success" @click="updateTable"><i class="fa fa-search"></i></b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-col cols="4">
                <b-form-group
                    id="fieldset-8"
                    label="Blood Sample or Aliquot Barcode"
                    label-for="search_keyword"
                >
                    <b-input-group>
                        <b-form-input
                            id="search_keyword"
                            v-model="search_keyword"
                            trim v-on:keydown.enter="updateTable"
                            placeholder="Search for blood sample or aliquot barcodes..."
                        ></b-form-input>
                        <b-input-group-append>
                            <b-button variant="success" @click="updateTable"><i class="fa fa-search"></i></b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>

            <b-col cols="2">
                <b-form-group
                    id="moh-zone-label"
                    label="MOH Zone"
                    label-for="moh-zone"
                >
                    <b-form-select id="moh-zone" v-model="moh" :options="mohOptions"></b-form-select>
                </b-form-group>
            </b-col>

            <b-col cols="2">
                <label for="bs_start_date">Start Date</label>
                <b-form-datepicker
                id="bs_start_date"
                v-model="bs_start_date"
                placeholder="Start Date"
                class="mb-2"
                reset-button
                >
                </b-form-datepicker>
            </b-col>
            <b-col lg="2">
                <label for="bs_end_date">End Date</label>
                <b-form-datepicker
                    id="bs_end_date"
                    :min="bs_start_date"
                    v-model="bs_end_date"
                    placeholder="End Date"
                    class="mb-2"
                    reset-button
                    :disabled='bs_start_date==""'
                >
                </b-form-datepicker>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="2" align-self="end">
                <b-button style="width: 90px;" class="mb-3" size="sm" @click="clearFields">
                    Clear
                </b-button>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <small>Total Results: {{ totalRows }} <i @click="downloadFile" class="fa fa-download text-success" v-b-modal.report-modal style="margin-left: 10px; cursor: pointer;" title="Download All"></i></small>
            </b-col>
        </b-row>
        <div style="width: 100%; overflow: auto;">
            <b-table
                ref="resultTable"
                striped
                hover
                selectable
                select-mode='single'
                @row-selected="onRowSelected"
                :items="getData"
                :current-page="currentPage"
                :per-page="perPage"
                :tbody-tr-class="'clickable'"
                :busy.sync="tableBusy"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
            >
            <!-- :fields="fields" -->
                <!-- Replace rows with a busy spinner while the table data is loading. -->
                <!-- https://bootstrap-vue.org/docs/components/table#table-busy-state -->
                <template #table-busy>
                <div class="text-danger my-2">
                    <b-spinner class="align-left"></b-spinner>
                    <strong>&nbsp;Loading...</strong>
                </div>
                </template>
            </b-table>
        </div>

        <b-row>
            <b-col cols="2">
                <b-form-group id="fieldset-2" label="Results Per Page" label-for="input-2">
                    <b-form-select id="input-2" v-model="perPage" :options="perPageOptions"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col class="my-1" cols="9" style="padding-top: 30px;">
                <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="fill"
                size="sm"
                class="my-0"
                ></b-pagination>
            </b-col>
        </b-row>
    </div>
</template>
  
<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";
import * as _ from "lodash";
import { mapGetters } from 'vuex';
import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";
  
export default {
mixins: [
    DashboardBaseTemplate,
],
components: {},
props: {},
data: function() {
    return {
        showModal: false,
        ModalMessage: '',
        sortBy: "subject_id",
        sortDesc: false,
        tableData: [],
        bs_start_date: '',
        bs_end_date: '',
        filterVisitType: 'All',
        visitTypeOptions: [
            'All',
            'Yearly',
            'Sick'
        ],
        filterSampleType: 'All',
        sampleTypeOptions: [
            'All',
            'T0',
            'T1',
            'T2',
            'Acute',
            'Convalescent',
            'Single'
        ],
        filterBoxNum: 'No Boxes',
        BoxNumOptions: [
            'No Boxes'
        ],
        filterFreezerNum: 'No Freezers',
        FreezerNumOptions: [
            'No Freezers'
        ],
        filterShelfNum: 'No Shelves',
        shelfNumOptions: [
            'No Shelves'
        ],
        filterStatus: 'All',
        statusOptions: [
            "All",
            "Not Received",
            "Received",
            "IgG ELISA",
            "iELISA",
            "FRNT",
            "RT PCR"
        ],
        moh: 'All',
        mohOptions: [
            'All',
            'Kelaniya',
            'Negombo',
            'Wattala'
        ],
        search_keyword: null,
        subject_id: null,
        // fields: [
        //     { key: 'blood_sample_id', sortable: true, tdClass: '' },
        //     { key: 'blood_sample_analysis_id', sortable: true, tdClass: '' },
        //     { key: 'aliquot1_id', sortable: true, tdClass: '' },
        //     { key: 'aliquot2_id', sortable: true, tdClass: '' },
        //     { key: 'aliquot3_id', sortable: true, tdClass: '' },
        //     { key: 'aliquot4_id', sortable: true, tdClass: '' },
        // ],
        totalRows: 0,
        currentPage: 1,
        perPage: 20,
        title: "",
        perPageOptions: [
            10,
            15,
            20,
            25,
            50
        ],
        tableBusy: false,
        fileDownload: false
    };
},
computed: {
    ...mapGetters("user", ["inMyGroups"]),
    processing_submit_disabled:function () {
        let disabled = false;
        _.each(this.form_fields, function(field) {
            if (field.value == null || field.value == '') {
                disabled = true;
            }
        });
        return disabled;
    }
},
watch: {
    filterVisitType: function(val){
        this.updateTable();
        this.filterSampleType = 'All';
        if (val == 'All') {
            this.sampleTypeOptions = [
                'All',
                'T0',
                'T1',
                'T2',
                'Acute',
                'Convalescent'
            ]
        } else if (val == 'Yearly') {
            this.sampleTypeOptions = [
                'All',
                'T0',
                'T1',
                'T2'
            ]
        } else if (val == 'Sick') {
            this.sampleTypeOptions = [
                'All',
                'Acute',
                'Convalescent'
            ]
        }
    },
    filterSampleType: function(){
        this.updateTable();
    },
    filterBoxNum: function(){
        this.updateTable();
    },
    filterFreezerNum: function(){
        this.updateTable();
    },
    filterShelfNum: function(){
        this.updateTable();
    },
    filterStatus: function(){
        this.updateTable();
    },
    subject_id: function(){
        this.updateTable();
    },
    search_keyword: function(){
        this.updateTable();
    },
    moh: function(){
        this.updateTable();
    },
    bs_start_date: function(){
        this.updateTable();
    },
    bs_end_date: function(){
        this.updateTable();
    }
},
created: function() {
    this.getDropdownOptions();
},
mounted: function() {},
methods: {
    getDropdownOptions() {
        return HTTP.get("srilanka/blood_sample/get_bsr_dropdown_options/").then((response) => {
            if (response.data.box_num_list.length > 0) {
                this.BoxNumOptions = response.data.box_num_list.sort();
                this.filterBoxNum = 'All';
            }
            if (response.data.freezer_num_list.length > 0) {
                this.FreezerNumOptions = response.data.freezer_num_list.sort();
                this.filterFreezerNum = 'All';
            }
            if (response.data.shelf_num_list.length > 0) {
                this.shelfNumOptions = response.data.shelf_num_list.sort();
                this.filterShelfNum = 'All';
            }
        })
        .catch((error) => {
            HandleErrors.HandleErrors(error);
        });
    },
    buildRequestParams () {
        let that = this;
        var params = new URLSearchParams();
        if(that.filterVisitType != 'All'){
            params.append('visit_type', that.filterVisitType.toLowerCase());
        }
        if(that.filterSampleType != 'All'){
            params.append('sample_type', that.filterSampleType.toLowerCase());
        }
        if(that.filterBoxNum != 'All' && that.filterBoxNum != 'No Boxes'){
            params.append('box_num', that.filterBoxNum);
        }
        if(that.filterFreezerNum != 'All' && that.filterFreezerNum != 'No Freezers'){
            params.append('freezer_num', that.filterFreezerNum);
        }
        if(that.filterShelfNum != 'All' && that.filterShelfNum != 'No Shelves'){
            params.append('shelf_num', that.filterShelfNum);
        }
        if(that.filterStatus != 'All'){
            params.append('status', that.filterStatus);
        }
        if(that.subject_id && that.subject_id != ''){
            params.append('subject_id', that.subject_id);
        }
        if(that.search_keyword && that.search_keyword != ''){
            params.append('search_keyword', that.search_keyword);
        }
        if(that.moh != 'All'){
            params.append('moh', that.moh);
        }
        if(that.bs_start_date && that.bs_start_date != ''){
            params.append('bs_start_date', that.bs_start_date);
        }
        if(that.bs_end_date && that.bs_end_date != ''){
            params.append('bs_end_date', that.bs_end_date);
        }
        return params;
    },
    getData: function(ctx) {
        let that = this;

        var params = that.buildRequestParams();
        params.append("current_page", ctx.currentPage);
        params.append("per_page", ctx.perPage);
        params.append("sort_by", ctx.sortBy);
        params.append("descending", ctx.sortDesc);

        return HTTP.get("srilanka/blood_sample/review/", {params: params})
            .then((response) => {
            that.tableData = response.data.row_data;
            that.totalRows = response.data.totalRows;
            return that.tableData;
        })
        .catch((error) => {
            HandleErrors.HandleErrors(error);
        });
    },
    downloadFile: function(){
        let that = this;
        var params = that.buildRequestParams();
        params.append("return_as", "csv");

        HTTP.get("srilanka/blood_sample/review/", {params: params}).then(response => {
            var fileURL = window.URL.createObjectURL(
                new Blob([response.data])
            );
            var fileLink = document.createElement("a");
            fileLink.href = fileURL;
            const now = new Date();
            fileLink.setAttribute("download", `SL_BloodSampleExport_${now}.csv`);
            document.body.appendChild(fileLink);
            fileLink.click();
        });
    },
    updateTable: function(){
      this.$refs.resultTable.refresh();
    },
    clearFields() {
        this.filterVisitType = 'All';
        this.filterSampleType = 'All';
        this.filterBoxNum = 'All';
        this.filterFreezerNum = 'All';
        this.filterShelfNum = 'All';
        this.filterStatus = 'All';
        this.subject_id = '';
        this.search_keyword = '';
        this.bs_start_date = '';
        this.bs_end_date = '';
        this.moh = '';
    },
    onRowSelected(items) {
        if(this.inMyGroups(['Blood Sample Manager'])) {
            if (items[0]['id'] != 'NA') {
                this.$router.push({name: 'blood-sample-edit', params: {id: items[0]['id'] }});
            } else {
                alert('Blood sample not yet received.');
            }
        } else {
            console.log('Not authorized to edit product blood sample analysis.')
        }
    },
}
};
</script>

<style scoped></style>
  